import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isUserSessionExpired } from '../../../../configuration/login/loginSlice';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { useGetInventoryVehiclesQuery } from '../../api/compoundManagementApi';
import { handleQueryError } from '../../notifications/ErrorNotification';
import {
    getCompoundManagementModalFilter,
    getDebouncedVinSearchFilter,
    getVehicleInventorySorting,
    mapInventoryVehiclesFilterQueryParams,
} from '../../reducers/compoundManagementSlice';
import { LoadMoreButton } from '../../sharedComponents/table/LoadMoreButton';
import { TableColumnSettingsState } from '../../sharedComponents/table/TableColumnSettingsState';
import { useSetUserTriggeredFetching } from '../../sharedComponents/userTriggeredFetchingHook';
import { CompoundManagementToolbar } from '../shared/toolbar/CompoundManagementToolbar';
import { StartBulkStatusUpdateButton } from '../shared/toolbar/StartBulkStatusUpdateButton';
import { VehicleInventoryTable } from './table/VehicleInventoryTable';
import { vehicleInventoryColumnDefinition } from './table/VehicleInventoryTableColumn';

export const DEFAULT_COMPOUND_INVENTORY_LIMIT_SIZE = 100;
export const MAX_COMPOUND_INVENTORY_LIMIT_SIZE = 1000;
const POLLING_INTERVAL = 60000;

export const VehicleInventory: FunctionComponent = () => {
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const filters = useAppSelector(getCompoundManagementModalFilter);
    const debouncedVinSearch = useAppSelector(getDebouncedVinSearchFilter);
    const sorting = useAppSelector(getVehicleInventorySorting);

    const [limit, setLimit] = useState(DEFAULT_COMPOUND_INVENTORY_LIMIT_SIZE);
    const nextLimit = limit + DEFAULT_COMPOUND_INVENTORY_LIMIT_SIZE;

    const {
        data: inventoryPage,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetInventoryVehiclesQuery(
        { ...mapInventoryVehiclesFilterQueryParams(filters, debouncedVinSearch), limit, sortBy: sorting },
        { pollingInterval: isSessionExpired ? undefined : POLLING_INTERVAL },
    );

    useSetUserTriggeredFetching({
        isFetching,
        depsWhichTriggerUserFetching: [sorting, filters, debouncedVinSearch],
    });

    if (isError) {
        console.error(error);
        handleQueryError(error);
    }

    if (isLoading) {
        return (
            <div data-testid="spinner">
                <Spinner />
            </div>
        );
    }

    if (inventoryPage === undefined) {
        return (
            <ErrorState
                headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                message={<FormattedMessage id="intl-msg:common-message.error.generic.message" />}
            />
        );
    }

    const inventoryVehicles = inventoryPage.items;
    return (
        <TableColumnSettingsState tableId="compound.inventory" columnDefinition={vehicleInventoryColumnDefinition}>
            <CompoundManagementToolbar bulkActionButton={<StartBulkStatusUpdateButton />} />
            <VehicleInventoryTable vehicles={inventoryVehicles} />
            {inventoryVehicles.length > 0 && (
                <LoadMoreButton
                    onLoadMore={() => setLimit(nextLimit)}
                    hasMoreToLoad={inventoryPage.hasMore}
                    limitReached={nextLimit > MAX_COMPOUND_INVENTORY_LIMIT_SIZE}
                />
            )}
        </TableColumnSettingsState>
    );
};
